/**
 * CHART COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass container with lensing
 * - Animated bar charts with spring physics
 * - Glass tooltips on hover
 * - Gradient fills with shimmer
 * - Responsive and accessible
 */

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../lib/utils'

interface ChartDataPoint {
  label: string
  value: number
  color?: string
  secondaryValue?: number
}

interface ChartProps {
  data: ChartDataPoint[]
  title?: string
  subtitle?: string
  height?: number
  showValues?: boolean
  showGrid?: boolean
  formatValue?: (value: number) => string
  variant?: 'bar' | 'horizontal'
}

const defaultColors = [
  'from-blue to-blue-dark',
  'from-green to-green-dark',
  'from-purple to-purple-dark',
  'from-orange to-orange-dark',
  'from-teal to-teal-dark',
  'from-pink to-pink-dark',
  'from-indigo to-indigo-dark'
]

export function Chart({
  data,
  title,
  subtitle,
  height = 200,
  showValues = true,
  showGrid = true,
  formatValue = (v) => `$${v.toLocaleString()}`,
  variant = 'bar'
}: ChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const maxValue = useMemo(() => Math.max(...data.map(d => d.value)), [data])

  // Generate grid lines
  const gridLines = useMemo(() => {
    const lines = []
    for (let i = 0; i <= 4; i++) {
      lines.push((maxValue / 4) * i)
    }
    return lines
  }, [maxValue])

  // Spring animation config
  const springConfig = {
    type: "spring",
    damping: 20,
    stiffness: 200,
    mass: 0.8
  }

  if (variant === 'horizontal') {
    return (
      <div className={cn(
        'liquid-material-regular liquid-lens rounded-2xl',
        'shadow-glass dark:shadow-dark-glass p-6'
      )}>
        {/* Header */}
        {(title || subtitle) && (
          <div className="mb-6">
            {title && (
              <h3 className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary tracking-tight mt-1">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Horizontal bars */}
        <div className="space-y-4">
          {data.map((item, index) => {
            const widthPercent = (item.value / maxValue) * 100
            const color = item.color || defaultColors[index % defaultColors.length]

            return (
              <div key={index} className="space-y-2">
                {/* Label and value */}
                <div className="flex items-center justify-between">
                  <span className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary tracking-tight">
                    {item.label}
                  </span>
                  {showValues && (
                    <span className="text-subheadline font-bold text-light-text-primary dark:text-dark-text-primary tabular-nums">
                      {formatValue(item.value)}
                    </span>
                  )}
                </div>

                {/* Bar */}
                <div className="h-3 rounded-full bg-black/[0.04] dark:bg-white/[0.06] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${widthPercent}%` }}
                    transition={{ ...springConfig, delay: index * 0.1 }}
                    className={cn(
                      'h-full rounded-full bg-gradient-to-r',
                      color
                    )}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      'liquid-material-regular liquid-lens rounded-2xl',
      'shadow-glass dark:shadow-dark-glass p-6'
    )}>
      {/* Header */}
      {(title || subtitle) && (
        <div className="mb-6">
          {title && (
            <h3 className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary tracking-tight mt-1">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Chart area */}
      <div className="relative" style={{ height: `${height}px` }}>
        {/* Grid lines */}
        {showGrid && (
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {gridLines.reverse().map((value, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-caption2 text-light-text-quaternary dark:text-dark-text-quaternary w-12 text-right tabular-nums">
                  {formatValue(value)}
                </span>
                <div className="flex-1 border-t border-dashed border-black/[0.06] dark:border-white/[0.06]" />
              </div>
            ))}
          </div>
        )}

        {/* Bars */}
        <div
          className="absolute inset-0 flex items-end justify-between gap-3"
          style={{ paddingLeft: showGrid ? '56px' : 0 }}
        >
          {data.map((item, index) => {
            const heightPercent = (item.value / maxValue) * 100
            const color = item.color || defaultColors[index % defaultColors.length]
            const isHovered = hoveredIndex === index

            return (
              <div
                key={index}
                className="flex-1 flex flex-col items-center gap-2 h-full justify-end"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Tooltip */}
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.9 }}
                      transition={{ duration: 0.15 }}
                      className={cn(
                        'absolute z-10 px-3 py-2 rounded-xl',
                        'liquid-material-thick liquid-lens',
                        'shadow-liquid-md dark:shadow-dark-liquid-md',
                        'whitespace-nowrap'
                      )}
                      style={{ bottom: `${heightPercent + 10}%` }}
                    >
                      <span className="text-subheadline font-bold text-light-text-primary dark:text-dark-text-primary tabular-nums">
                        {formatValue(item.value)}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bar */}
                <motion.div
                  initial={{ height: 0 }}
                  animate={{
                    height: `${heightPercent}%`,
                    scale: isHovered ? 1.05 : 1
                  }}
                  transition={{ ...springConfig, delay: index * 0.08 }}
                  className="w-full relative group cursor-pointer"
                >
                  {/* Bar fill */}
                  <div
                    className={cn(
                      'w-full h-full rounded-xl bg-gradient-to-t',
                      color,
                      'transition-all duration-fast',
                      isHovered && 'shadow-liquid-md dark:shadow-dark-liquid-md'
                    )}
                  />

                  {/* Shimmer effect on hover */}
                  <div className={cn(
                    'absolute inset-0 rounded-xl overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-fast',
                    'bg-gradient-to-t from-transparent via-white/20 to-white/30 dark:via-white/10 dark:to-white/15'
                  )} />
                </motion.div>

                {/* Label */}
                <span className="text-caption1 font-medium text-light-text-tertiary dark:text-dark-text-tertiary text-center tracking-tight">
                  {item.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * DonutChart - Circular progress/distribution chart
 */
interface DonutChartProps {
  data: Array<{ label: string; value: number; color: string }>
  title?: string
  centerLabel?: string
  centerValue?: string
  size?: number
}

export function DonutChart({
  data,
  title,
  centerLabel,
  centerValue,
  size = 200
}: DonutChartProps) {
  const total = useMemo(() => data.reduce((sum, item) => sum + item.value, 0), [data])
  const strokeWidth = size * 0.15
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  let cumulativePercent = 0

  return (
    <div className={cn(
      'liquid-material-regular liquid-lens rounded-2xl',
      'shadow-glass dark:shadow-dark-glass p-6'
    )}>
      {title && (
        <h3 className="text-title3 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight mb-6">
          {title}
        </h3>
      )}

      <div className="flex items-center justify-center gap-8">
        {/* Donut SVG */}
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} className="-rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-black/[0.04] dark:text-white/[0.06]"
            />

            {/* Data segments */}
            {data.map((item, index) => {
              const percent = item.value / total
              const offset = circumference * cumulativePercent
              const length = circumference * percent
              cumulativePercent += percent

              return (
                <motion.circle
                  key={index}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                  initial={{ strokeDasharray: `0 ${circumference}` }}
                  animate={{ strokeDasharray: `${length} ${circumference - length}` }}
                  transition={{ duration: 0.8, delay: index * 0.15, ease: [0.23, 1, 0.32, 1] }}
                />
              )
            })}
          </svg>

          {/* Center content */}
          {(centerLabel || centerValue) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {centerValue && (
                <span className="text-title1 font-bold text-light-text-primary dark:text-dark-text-primary tabular-nums">
                  {centerValue}
                </span>
              )}
              {centerLabel && (
                <span className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary">
                  {centerLabel}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-3">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <div>
                <p className="text-subheadline font-medium text-light-text-primary dark:text-dark-text-primary">
                  {item.label}
                </p>
                <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary tabular-nums">
                  {((item.value / total) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * SparklineChart - Minimal inline chart
 */
interface SparklineChartProps {
  data: number[]
  color?: string
  height?: number
  showFill?: boolean
}

export function SparklineChart({
  data,
  color = '#007AFF',
  height = 40,
  showFill = true
}: SparklineChartProps) {
  const maxValue = Math.max(...data)
  const minValue = Math.min(...data)
  const range = maxValue - minValue || 1

  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * 100
    const y = ((maxValue - value) / range) * height
    return `${x},${y}`
  }).join(' ')

  const fillPoints = `0,${height} ${points} 100,${height}`

  return (
    <svg
      width="100%"
      height={height}
      className="overflow-visible"
      preserveAspectRatio="none"
    >
      {/* Fill gradient */}
      {showFill && (
        <>
          <defs>
            <linearGradient id={`sparkline-gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.3" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.polygon
            points={fillPoints}
            fill={`url(#sparkline-gradient-${color})`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
        </>
      )}

      {/* Line */}
      <motion.polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      />

      {/* End dot */}
      <motion.circle
        cx="100"
        cy={((maxValue - data[data.length - 1]) / range) * height}
        r="3"
        fill={color}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.8, type: "spring", damping: 15 }}
      />
    </svg>
  )
}
